import React from 'react';
import { Card, CardBody, Col } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Users, Tool, Calendar, MapPin } from 'react-feather';
import { useAuth } from '../../../Context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';

interface StatisticsProps {
    statistics: {
        vehicles: { total: number; active: number; inactive: number; maintenance: number; sold: number };
        drivers: { total: number; active: number; inactive: number };
        maintenance: { pending: number; overdue: number };
        renewals: { upcoming: number; insurance: number; rc: number; license: number; permit?: number; next_service?: number };
        branches: { total: number; active: number };
    };
}

export default function StatisticsCards({ statistics }: StatisticsProps) {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();


    // Dynamic Subtitle Logic for Renewals
    const renewalItems = [
        { label: 'Insurance', short: 'Ins', count: statistics.renewals.insurance },
        { label: 'RC', short: 'RC', count: statistics.renewals.rc },
        { label: 'License', short: 'Lic', count: statistics.renewals.license },
        { label: 'Permit', short: 'Pmt', count: statistics.renewals.permit || 0 },
        { label: 'Service', short: 'Svc', count: statistics.renewals.next_service || 0 },
    ].filter(item => item.count > 0);

    let renewalSubtitle = '';
    if (renewalItems.length === 0) {
        renewalSubtitle = 'No upcoming renewals';
    } else if (renewalItems.length <= 3) {
        renewalSubtitle = renewalItems.map(item => `${item.count} ${item.label}`).join(', ');
    } else {
        renewalSubtitle = renewalItems.map(item => `${item.count} ${item.short}`).join(', ');
    }

    const cards = [
        {
            title: 'Total Vehicles',
            value: statistics.vehicles.total,
            subtitle: `${statistics.vehicles.active} Active`,
            icon: TrendingUp,
            color: '#2563eb',
            bgColor: '#eff6ff',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            onClick: () => {
                if (hasPermission('vehicle.view')) {
                    navigate(`${process.env.PUBLIC_URL}/vehicle`);
                } else {
                    toast.error('Access Denied: You do not have permission to view Vehicles.');
                }
            },
        },
        {
            title: 'Total Drivers',
            value: statistics.drivers.total,
            subtitle: `${statistics.drivers.active} Active`,
            icon: Users,
            color: '#10b981',
            bgColor: '#f0fdf4',
            gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            onClick: () => {
                if (hasPermission('driver.view')) {
                    navigate(`${process.env.PUBLIC_URL}/driver`);
                } else {
                    toast.error('Access Denied: You do not have permission to view Drivers.');
                }
            },
        },
        {
            title: 'Total Branches',
            value: statistics.branches ? statistics.branches.total : 0,
            subtitle: statistics.branches ? `${statistics.branches.active} Active` : '0 Active',
            icon: MapPin,
            color: '#f59e0b',
            bgColor: '#fffbeb',
            gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            onClick: () => {
                if (hasPermission('branch.view')) {
                    navigate(`${process.env.PUBLIC_URL}/branch`);
                } else {
                    toast.error('Access Denied: You do not have permission to view Branches.');
                }
            },
        },
        {
            // title: 'Upcoming Renewals',
            title: 'Renewals Due',
            value: statistics.renewals.upcoming,
            subtitle: renewalSubtitle,
            icon: Calendar,
            color: '#8b5cf6',
            bgColor: '#f5f3ff',
            gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            onClick: () => {
                if (hasPermission('renewal-report.view')) {
                    navigate(`${process.env.PUBLIC_URL}/renewal_report`);
                } else {
                    toast.error('Access Denied: You do not have permission to view Renewal Reports.');
                }
            },
        },
    ];

    return (
        <>
            {cards.map((card, index) => (
                <Col xs={12} sm={6} md={6} lg={6} xl={3} key={index} className="mb-4 px-2">
                    <Card
                        className="stat-card"
                        style={{
                            cursor: 'pointer',
                            '--card-color': card.color,
                            '--card-color-light': card.bgColor,
                        } as React.CSSProperties}
                        onClick={card.onClick}
                    >
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between w-100">
                                <div className="flex-grow-1">
                                    <p className="stat-label mb-2" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                        {card.title}
                                    </p>
                                    <h2 className="stat-value mb-0">
                                        {card.value.toLocaleString()}
                                    </h2>
                                    <p className="stat-subtitle mb-0" style={{ color: card.color }}>
                                        <small>{card.subtitle}</small>
                                    </p>
                                </div>
                                <div
                                    className="stat-card-icon"
                                    style={{
                                        background: card.gradient,
                                        boxShadow: `0 4px 14px 0 ${card.color}40`
                                    }}
                                >
                                    <card.icon size={28} color="white" strokeWidth={2.5} />
                                </div>
                            </div>


                        </CardBody>
                    </Card>
                </Col>
            ))}
            <ToastContainer />
        </>
    );
}

